import React, { useState, useMemo } from 'react';
import { IconButton, Tooltip, Paper, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  GridValueFormatterParams,
  GridRenderCellParams,
  GridValueGetterParams,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import { useNavigate } from 'react-router-dom';
import { User as PersonIcon } from 'react-feather';
import moment from 'moment';
import { FullScreen } from '@pv/common/components';
import { ADMIN_ORGANIZATIONS_QUERY } from './graphql';
import { AdminReport, SearchBar } from '../reports';

export const OrganizationsTable = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState<string>('');

  const additionalQueryVariables = useMemo(
    () => ({
      search: search || undefined,
    }),
    [search]
  );

  const renderSearchBar = () => (
    <SearchBar
      onSearchChange={setSearch}
      placeholder="Search organizations by name..."
    />
  );

  const allColumns = [
    {
      field: 'id',
      headerName: 'ID',
      sortable: true,
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => (
        <Link
          component={RouterLink}
          to={`/admin/organizations/${params.row.id}/info`}
          underline="hover"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          {params.row.name}
        </Link>
      ),
    },
    {
      field: 'stripeCustomerId',
      headerName: 'Stripe Customer ID',
      flex: 1,
      sortable: false,
    },
    {
      field: 'hubspotDealId',
      headerName: 'HubSpot Deal ID',
      flex: 1,
      sortable: false,
    },
    {
      field: 'lastInvoiceNumber',
      headerName: 'Num Events',
      flex: 1,
      type: 'number',
      sortable: false,
    },
    {
      field: 'numUsers',
      headerName: 'Num Users',
      flex: 1,
      type: 'number',
      sortable: false,
      valueGetter: (params: GridValueGetterParams) =>
        params.row.users?.length || 0,
    },
    {
      field: 'numVenues',
      headerName: 'Num Venues',
      flex: 1,
      type: 'number',
      sortable: false,
      valueGetter: (params: GridValueGetterParams) =>
        params.row.venues?.length || 0,
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      flex: 1,
      sortable: true,
      valueFormatter: (params: GridValueFormatterParams) =>
        params.value ? moment(params.value).format('MM/DD/YYYY') : '-',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title="Login as User">
          <IconButton
            size="small"
            href={params.row.users?.[0]?.magicLoginLink}
            color="primary"
            onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
              event.stopPropagation();
            }}
          >
            <PersonIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <FullScreen paddingBottom={24}>
        {({ height }) => (
          <Paper sx={{ height, width: '100%' }}>
            <AdminReport
              reportName="adminOrganizations"
              columns={allColumns}
              title="Organizations"
              toolbar={true}
              query={ADMIN_ORGANIZATIONS_QUERY}
              additionalQueryVariables={additionalQueryVariables}
              renderCustomSearchBar={renderSearchBar}
              onRowClick={(params: GridRowParams) => {
                navigate(`/admin/organizations/${params.row.id}/info`);
              }}
            />
          </Paper>
        )}
      </FullScreen>
    </>
  );
};

export default OrganizationsTable;
